<template>
  <md-card>
    <md-card-content>
      <md-table
          :value="localTableData"
          :md-sort.sync="currentSort"
          :md-sort-order.sync="currentSortOrder"
          :md-sort-fn="noopSort"
          class="paginated-table table-striped table-hover"
      >
        <md-table-toolbar>
          <md-field>
            <label for="pages">Per page</label>
            <md-select
                v-model="pagination.perPage"
                name="pages"
                @md-selected="onPerPageChange"
            >
              <md-option
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
              >
                {{ item }}
              </md-option>
            </md-select>
          </md-field>

          <md-field>
            <md-input
                type="search"
                class="mb-3"
                clearable
                style="width: 200px"
                placeholder="Search records"
                v-model="searchQuery"
                @input="debouncedSearch"
            />
          </md-field>
        </md-table-toolbar>

        <md-table-empty-state
            md-label="No schedule found"
            :md-description="`No Schedule found for '${searchQuery}'. Try a different search term or run generate schedule function.`"
        >
          <md-button class="md-primary md-raised" @click="$emit('generateSchedules', guidance)">
            Generate Takeons
          </md-button>
        </md-table-empty-state>

        <md-table-row slot="md-table-row" slot-scope="{ item }">
          <md-table-cell md-label="Shift" md-sort-by="shift.name">
            {{ item.shift ? item.shift.name : '' }}
          </md-table-cell>
          <md-table-cell md-label="Day Type" md-sort-by="schedule.day_type">
            {{ item.schedule ? item.schedule.day_type : '' }}
          </md-table-cell>
          <md-table-cell md-label="Shift Start" md-sort-by="schedule_start_time">
            {{ item.schedule ? item.schedule.start_time : '' }}
          </md-table-cell>
          <md-table-cell md-label="Shift End" md-sort-by="schedule_end_time">
            {{ item.schedule ? item.schedule.end_time : '' }}
          </md-table-cell>
          <md-table-cell md-label="Workplace" md-sort-by="workplace.name">
            {{ item.workplace ? item.workplace.name : '' }}
          </md-table-cell>
          <md-table-cell md-label="Activity" md-sort-by="activity.name">
            {{ item.activity ? item.activity.name : '' }}
          </md-table-cell>
          <md-table-cell md-label="Takeon Number" md-sort-by="takeon_number">
            {{ item.takeon_number }}
          </md-table-cell>
          <md-table-cell md-label="Name" md-sort-by="labourer.name">
            {{ item.labourer ? item.labourer.name : '' }}
          </md-table-cell>
          <md-table-cell md-label="Status" md-sort-by="status">
            {{ item.status }}
          </md-table-cell>
          <md-table-cell md-label="Actions">
            <md-button class="md-just-icon md-info md-simple" @click="handleLike(item)">
              <md-icon>visibility</md-icon>
            </md-button>
          </md-table-cell>
        </md-table-row>
      </md-table>
    </md-card-content>

    <md-card-actions md-alignment="space-between">
      <div class="card-category">
        Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
      </div>
      <pagination
          class="pagination-no-border pagination-success"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
          @input="getScheduleList"
      />
    </md-card-actions>
  </md-card>
</template>

<script>
import { Pagination } from '@/components';

export default {
    name: 'GuidanceTakeonTable',
    components: { Pagination },
    props: {
        guidance: { type: Object, required: true }
    },
    data () {
        return {
            localTableData: [],
            loading: false,

            // server-driven sort
            currentSort: 'schedule_start_time',
            currentSortOrder: 'asc',

            // server-driven pagination
            pagination: {
                perPage: 20,
                currentPage: 1,
                perPageOptions: [5, 10, 25, 50],
                total: 0
            },

            // server-driven search
            searchQuery: '',
            debounceTimer: null
        };
    },
    computed: {
        total () {
            return this.pagination.total;
        },
        from () {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        to () {
            const high = this.from + this.pagination.perPage;
            return Math.min(high, this.total);
        }
    },
    mounted () {
        this.getScheduleList();
    },
    methods: {
    // no-op sorter so md-table doesn't reorder locally; server already sorted
        noopSort (rows) {
            return rows;
        },

        onPerPageChange () {
            this.pagination.currentPage = 1;
            this.getScheduleList();
        },

        debouncedSearch () {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.pagination.currentPage = 1;
                this.getScheduleList();
            }, 300);
        },

        async getScheduleList () {
            this.loading = true;

            // If your backend follows JSON:API, use `sort` like "-field" for desc.
            // Otherwise, switch to { sort_field, sort_dir } as your API expects.
            const sortParam =
          (this.currentSortOrder === 'desc' ? '-' : '') + this.currentSort;

            const params = {
                include: 'schedule,workplace,shift,activity,labourer',
                filter: {
                    ...(this.guidance ? { guidance: this.guidance.id } : {}),
                    ...(this.searchQuery ? { queryKey: this.searchQuery } : {})
                },
                page: {
                    number: this.pagination.currentPage,
                    size: this.pagination.perPage
                },
                sort: sortParam
            };

            try {
                await this.$store.dispatch('labourerSchedules/list', params);

                // Expecting your Vuex module to expose paginated results + total
                this.localTableData = this.$store.getters['labourerSchedules/list'];
                this.pagination.total =
            this.$store.getters['labourerSchedules/listTotal'] || 0;
            } catch (e) {
                this.notifyException && this.notifyException(e);
            } finally {
                this.loading = false;
            }
        },

        handleLike (item) {
            this.$emit('goToLabourerSchedule', item.id);
        }
    },
    watch: {
    // refetch when header sort changes
        currentSort () {
            this.pagination.currentPage = 1;
            this.getScheduleList();
        },
        currentSortOrder () {
            this.pagination.currentPage = 1;
            this.getScheduleList();
        }
    }
};
</script>

<style scoped>
/* optional: add any loading state styles, etc. */
</style>
